import { listNames, MarkupError, type MarkupPosition } from "./errors.js";
import type { XmlElement } from "./xml.js";
import { parseMarkup } from "./xml.js";

/** How deep one value may reference another before it is called a cycle. */
const MAX_DEPTH = 16;

/**
 * Named values declared outside the layout and substituted into it.
 *
 * The thing a layout accumulates that is not layout: a product name, a gutter that four panels
 * happen to share, a color the brand owns, a tooltip written once and shown in three places.
 * Left inline they are scattered facts nobody can change confidently; gathered into a `<Values>`
 * block at the top they are a settings sheet, and the layout below reads as structure again.
 *
 * They are **text**, substituted before an attribute is read, which is what makes them work
 * everywhere at once: the attribute's own type coerces the result, so one value can be a length
 * in one place and a caption in another without declaring which it is.
 *
 * ```xml
 * <Values>
 *   <Value name="brand">Notes</Value>
 *   <Value name="gutter">12</Value>
 * </Values>
 * <Window title="{brand}">
 *   <VStack gap="{gutter}" padding="{gutter}">…</VStack>
 * </Window>
 * ```
 *
 * This is substitution, not binding. It happens once, at load, against text that was written
 * down — there is no expression to evaluate, nothing to observe, and nothing that can go stale.
 */
export class StaticValues {
  #byName = new Map<string, string>();

  set(name: string, text: string): void {
    this.#byName.set(name, text);
  }

  has(name: string): boolean {
    return this.#byName.has(name);
  }

  /** The declared text, before any references inside it are resolved. */
  raw(name: string): string | undefined {
    return this.#byName.get(name);
  }

  names(): string[] {
    return [...this.#byName.keys()].sort();
  }

  get empty(): boolean {
    return this.#byName.size === 0;
  }

  /** Copy another table's entries over this one's. */
  merge(other: StaticValues): void {
    for (const name of other.names()) this.set(name, other.raw(name)!);
  }

  /**
   * Substitute `{name}` references in one piece of text.
   *
   * `{{` and `}}` are literal braces, for the rare attribute that wants one. A lone `}` is
   * literal too, so a stray brace in a caption is not an error nobody can explain.
   *
   * An undeclared name **throws**, rather than warning and leaving the reference on screen. It
   * is the same failure as a handler that was never passed: a typo whose other outcome is a
   * `{brandName}` shipped to somebody's users, which is worse than not loading at all.
   */
  resolve(text: string, at: MarkupPosition, depth = 0): string {
    if (!text.includes("{") && !text.includes("}")) return text;
    if (depth > MAX_DEPTH) {
      throw new MarkupError(
        `"${text}" is a cycle: a value cannot reference itself, however indirectly`,
        at,
      );
    }

    let out = "";
    let i = 0;
    while (i < text.length) {
      const ch = text[i]!;
      if (ch === "{" && text[i + 1] === "{") {
        out += "{";
        i += 2;
        continue;
      }
      if (ch === "}" && text[i + 1] === "}") {
        out += "}";
        i += 2;
        continue;
      }
      if (ch !== "{") {
        out += ch;
        i++;
        continue;
      }

      const end = text.indexOf("}", i + 1);
      if (end < 0) {
        throw new MarkupError(`"${text}" has a "{" with no "}"; write "{{" for a literal brace`, at);
      }
      const name = text.slice(i + 1, end).trim();
      if (name === "") {
        throw new MarkupError(`"{}" names no value; write "{{}}" for literal braces`, at);
      }
      const value = this.#byName.get(name);
      if (value === undefined) {
        throw new MarkupError(
          `no value is named "${name}"; this document declares ${listNames(this.names())}`,
          at,
        );
      }
      // Resolved on the way out rather than at declaration, so a value may be written in terms
      // of another whichever order the two happen to appear in.
      out += this.resolve(value, at, depth + 1);
      i = end + 1;
    }
    return out;
  }

  /** Read one `<Values>` element's children into this table. */
  addFrom(values: XmlElement): void {
    for (const child of values.children) {
      if (child.name !== "Value") {
        throw new MarkupError(`<Values> holds <Value> elements, not <${child.name}>`, child);
      }
      const name = child.attributes.find((a) => a.name === "name");
      if (!name) throw new MarkupError(`<Value> needs a "name"`, child);
      const attribute = child.attributes.find((a) => a.name === "value");
      this.set(name.value, attribute ? attribute.value : child.text);
    }
  }
}

/**
 * Read a standalone `<Values>` document.
 *
 * Separate from a layout for the same reason a stylesheet is: a set of windows that share a
 * vocabulary — product names, spacing, the strings of one language — should declare it once.
 */
export function loadValues(xml: string): StaticValues {
  const root = parseMarkup(xml);
  if (root.name !== "Values") {
    throw new MarkupError(`a values document's root element is <Values>, not <${root.name}>`, root);
  }
  const table = new StaticValues();
  table.addFrom(root);
  return table;
}

/** Split `<Values>` out of an element's children, folding it into a table. */
export function takeValues(root: XmlElement, into: StaticValues): StaticValues {
  const kept: XmlElement[] = [];
  for (const child of root.children) {
    if (child.name === "Values") into.addFrom(child);
    else kept.push(child);
  }
  root.children = kept;
  return into;
}

/**
 * Substitute every reference in a tree, in place.
 *
 * One pass up front rather than a call at each place text is read, so an element nobody in this
 * library wrote — a `registerElement` from an application — gets substitution without knowing
 * the feature exists. `<Styles>` is skipped: a style's attributes are substituted where they are
 * applied, so that a sheet shared between documents can reference each one's own values.
 */
export function substituteTree(element: XmlElement, values: StaticValues): void {
  // Deliberately not skipped when nothing is declared. A `{` has to mean the same thing in every
  // document, or `{brand}` would be an error in a file with a `<Values>` block and silent text
  // in one without — a rule whose outcome depends on a part of the file you are not looking at.
  if (element.name === "Styles") return;
  for (const attribute of element.attributes) {
    attribute.value = values.resolve(attribute.value, attribute);
  }
  if (element.text) element.text = values.resolve(element.text, element);
  for (const child of element.children) substituteTree(child, values);
}
